import api from "../api.js";

/**
 * Bookables service
 *
 * Methods for interacting with the Bookables API.
 *
 * @package HOPS
 * @subpackage Bookables
 * @author Heron Web Ltd
 * @copyright Heritage Operations Processing Limited
 */
class BookablesService {

	/**
	 * Create a new booking.
	 *
	 * @param {Object} data API booking creation object to submit
	 * @return {Promise}
	 */
	static createBooking(data) {
		return api.call({
			url: `/api/bookables/bookings`,
			method: "POST",
			data
		}).then(({data}) => data);
	}


	/**
	 * Update an existing booking by its ID.
	 *
	 * @param {Integer} id
	 * @param {Object} data
	 * @return {Promise}
	 */
	static updateBooking(id, data) {
		return api.call({
			url: `/api/bookables/bookings/${id}`,
			method: "PUT",
			data
		}).then(({data}) => data);
	}


	/**
	 * Delete an existing booking by its ID.
	 *
	 * @param {Integer} id
	 * @return {Promise}
	 */
	static deleteBooking(id) {
		return api.call({
			url: `/api/bookables/bookings/${id}`,
			method: "DELETE"
		}).then(({data}) => data);
	}


	/**
	 * Get the actively authenticated user's bookable unit bookings.
	 *
	 * When `authored` is `false`, gets the bookings which have been 
	 * created *for* the user (they are named on the booking).
	 *
	 * When `authored` is `true`, gets the bookings the user's created.
	 * 
	 * @param {Boolean} optional options.Authored (See above) (`false`)
	 * @return {Promise} Resolves with API response data
	 */
	static getActiveUserBookings({Authored=false}={}) {
		return api.call({
			url: `/api/bookables/bookings/mine`,
			params: {Authored: (Authored ? 1 : undefined)}
		}).then(({data}) => data);
	}


	/**
	 * Query all the bookings at the actively authenticated user.
	 *
	 * @param {Object} params optional API query parameters
	 * @return {Promise} Resolves with API response data
	 */
	static getBookings(params=null) {
		return api.call({
			url: `/api/bookables/bookings`,
			params
		}).then(({data}) => data);
	}


	/**
	 * Clear the error state from a booking's Keycafe access submission, 
	 * allowing it to be retried.
	 *
	 * @param {Integer} bookingId
	 * @return {Promise}
	 */
	static scheduleBookingKeycafeAccessResubmission(bookingId) {
		return api.call({
			url: `/api/bookables/bookings/${bookingId}/keycafe/error`,
			method: "DELETE"
		});
	}


	/**
	 * Check whether a unit at the active user's organisation 
	 * is available to be booked over a given timeframe.
	 *
	 * @param {Integer} Unit ID
	 * @param {Object} Timespan `Start`/`End` date/time strings
	 * @param {Integer} optional ExcludedBookingId See API docs (`false`)
	 * @return {Promise} Resolves with API response data
	 */
	static checkUnitAvailability(Unit, Timespan, ExcludedBookingId=undefined) {
		return api.call({
			url: `/api/bookables/units/${Unit}/availability`,
			params: {Timespan, ExcludedBookingId}
		}).then(({data}) => data);
	}


	/**
	 * Query all the bookable units at the active user's organisations 
	 * and get the ones the user can access, with the bookings against 
	 * them in the seven day period after a target date.
	 * 
	 * @param {String} StartDate YYYY-MM-DD
	 * @return {Promise} Resolves with API response data
	 */
	static getUnitAvailabilityChart(StartDate) {
		return api.call({
			url: `/api/bookables/units/availability/chart`,
			params: {StartDate}
		}).then(({data}) => data);
	}


	/**
	 * Get bookable units at the active user's organisation 
	 * including indication of availability in a given timeframe.
	 *
	 * @param {Object} Timespan optional `Start`/`End` date/time strings
	 * @return {Promise} Resolves with API response data
	 */
	static getUnitsWithAvailabilities(Timespan=null) {
		return api.call({
			url: `/api/bookables/units/availability`,
			params: {Timespan}
		}).then(({data}) => data);
	}


	/**
	 * Get whether a unit can be parented to another unit.
	 *
	 * @param {Object} unit
	 * @param {Object} targetParent
	 * @return {Boolean}
	 */
	static canUnitBeParentedTo(unit, targetParent) {

		const targetParentAncestorIds = [];
		let parent = targetParent;

		while (parent.Parent) {
			targetParentAncestorIds.push(parent.Parent.Id);
			parent = parent.Parent;
		}

		return ((unit.Id !== targetParent.Id) && !targetParentAncestorIds.includes(unit.Id));

	}


	/**
	 * Get the IDs of all the units that are parents to a given unit.
	 *
	 * @param {Object} unit
	 * @return {Array<Integer>}
	 */
	static getUnitParentIds(unit) {

		let parent = unit;
		const parentIds = [];

		while (parent.Parent) {
			parentIds.push(parent.Parent.Id);
			parent = parent.Parent;
		}

		return parentIds;

	}


	/**
	 * Get the number of parents that exist above a bookable unit.
	 *
	 * @param {Object} unit
	 * @return {Integer}
	 */
	static getUnitParentLevel(unit) {

		let parentLevel = 0;
		let parent = unit;

		while (parent.Parent) {
			parentLevel++;
			parent = parent.Parent;
		}

		return parentLevel;

	}


	/**
	 * Map an array of bookable unit objects into an object 
	 * that's indexed by unit ID with each value being an 
	 * array of direct children of that unit.
	 *
	 * @param {Array<Object>} units
	 * @return {Object}
	 */
	static getUnitsTreeLeaves(units) {

		/**
		 * Tree
		 *
		 * Parent ID => array of children units
		 */
		const tree = {};

		/**
		 * Known tree leaves
		 *
		 * Parent ID => array of child IDs
		 */
		const knownLeaves = {};

		/**
		 * Recursive utility function to add a unit 
		 * and its parents into the hierarchical tree
		 */
		const addLeafToTree = unit => {

			const id = unit.Id;
			const leaf = (unit.Parent?.Id || null);

			tree[leaf] ||= [];
			knownLeaves[leaf] ||= [];

			if (!knownLeaves[leaf].includes(id)) {
				tree[leaf].push(unit);
				knownLeaves[leaf].push(id);
			}

			if (leaf) {
				addLeafToTree(unit.Parent);
			}

		};

		/**
		 * Map our units into the tree hierarchy
		 */
		for (const unit of units) {
			addLeafToTree(unit);
		}

		return tree;

	}


	/**
	 * Map an array of bookable unit objects into a new array of 
	 * objects sorted so that iterating the array results in the 
	 * hierarchival tree being traversed in order.
	 *
	 * @param {Array<Object>} units
	 * @param {Boolean} unitIsTree optional `units` is result of `getUnitsTreeLeaves()` (`false`)
	 * @return {Object}
	 */
	static getUnitsTreeLeavesFlattened(units, unitsIsTree=false) {

		/**
		 * Map the units to their tree hierarchy
		 */
		const tree = (!unitsIsTree ? this.getUnitsTreeLeaves(units) : units);

		/**
		 * Flattened tree to return
		 */
		const treeFlattened = [];

		/**
		 * Recursive utility function to add a unit and all its 
		 * children into our flattened tree that we're going to render
		 */
		const addUnitToFlattenedTree = unit => {
			treeFlattened.push(unit);
			for (const child of (tree[unit.Id] || [])) {
				addUnitToFlattenedTree(child);
			}
		};

		/**
		 * Add all the top-level units to the flattened tree (recursively)
		 */
		for (const unit of (tree[null] || [])) {
			addUnitToFlattenedTree(unit);
		}

		return treeFlattened;

	}


	/**
	 * Get a unit at the active user's organisation by its ID.
	 *
	 * @param {Integer} id
	 * @return {Promise}
	 */
	static getUnit(id) {
		return api.call({url: `/api/bookables/units/${id}`}).then(({data}) => data);
	}


	/**
	 * Get all units at the active user's organisation.
	 *
	 * @param {Object} params API request parameters
	 * @return {Promise} Resolves with API response data
	 */
	static getUnits(params) {
		return api.call({url: `/api/bookables/units`, params}).then(({data}) => data);
	}


	/**
	 * Get all the units that can be booked by the authenticated user.
	 *
	 * @return {Promise} Resolves with API response data
	 */
	static getUnitsBookableByAuthUser() {
		return api.call({url: `/api/bookables/units/bookable/mine`}).then(({data}) => data);
	}


	/**
	 * Create a unit at the active user's organisation.
	 *
	 * @param {Object} data
	 * @return {Promise}
	 */
	static createUnit(data) {
		return api.call({url: `/api/bookables/units`, data, method: "POST"});
	}


	/**
	 * Update a unit at the active user's organisation by its ID.
	 *
	 * @param {Integer} id
	 * @param {Object} data
	 * @return {Promise}
	 */
	static updateUnit(id, data) {
		return api.call({url: `/api/bookables/units/${id}`, data, method: "PUT"});
	}


	/**
	 * Get a unit type at the active user's organisation by its ID.
	 *
	 * @param {Integer} id
	 * @return {Promise}
	 */
	static getUnitType(id) {
		return api.call({url: `/api/bookables/types/${id}`}).then(({data}) => data);
	}


	/**
	 * Get all unit types at the active user's organisation.
	 *
	 * @param {Object} params API request parameters
	 * @return {Promise} Resolves with API response data
	 */
	static getUnitTypes(params) {
		return api.call({url: `/api/bookables/types`, params}).then(({data}) => data);
	}


	/**
	 * Create a unit type at the active user's organisation.
	 * 
	 * @param {Object} data
	 * @return {Promise}
	 */
	static createUnitType(data) {
		return api.call({url: `/api/bookables/types`, data, method: "POST"});
	}


	/**
	 * Update a unit type at the active user's organisation by its ID.
	 *
	 * @param {Integer} id
	 * @param {Object} data
	 * @return {Promise}
	 */
	static updateUnitType(id, data) {
		return api.call({url: `/api/bookables/types/${id}`, data, method: "PUT"});
	}

}

export default BookablesService;
